import { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { 
  Ticket, Clock, CheckCircle, XCircle, Warning, User, 
  CaretLeft, CaretRight, MagnifyingGlass, Funnel, PlayCircle, 
  UserPlus, UserMinus, ArrowsClockwise, SpinnerGap, UserCircle,
  Lightning, Broom
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import InfoTooltip from '../components/InfoTooltip';
import EmptyState from '../components/EmptyState';

const PAGE_SIZE = 20;

export default function ProvisioningTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [sourceFilter, setSourceFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assignedToMeFilter, setAssignedToMeFilter] = useState(false);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoResolvePreview, setAutoResolvePreview] = useState(null);
  const [showAutoResolveDialog, setShowAutoResolveDialog] = useState(false);
  const [autoResolving, setAutoResolving] = useState(false);

  const loadTickets = useCallback(async () => {
    try {
      const params = {
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE
      };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (assignedToMeFilter) params.assigned_to_me = true;
      if (search) params.search = search;

      const res = await API.get('/provisioning-tickets', { params });
      setTickets(res.data.items);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load tickets');
    }
  }, [page, statusFilter, sourceFilter, priorityFilter, assignedToMeFilter, search]);

  const loadStats = async () => {
    try {
      const res = await API.get('/provisioning-tickets/stats');
      setStats(res.data);
    } catch (err) {
      void 0;
    }
  };

  useEffect(() => {
    loadTickets();
    loadStats();
  }, [loadTickets]);

  const openTicket = (ticket) => {
    setSelectedTicket(ticket);
    setResolutionNotes(ticket.resolution_notes || '');
    setShowTicketDialog(true);
  };

  const handleAssign = async (ticketId) => {
    try {
      await API.put(`/provisioning-tickets/${ticketId}/assign`);
      toast.success('Ticket assigned to you');
      loadTickets();
      loadStats();
      if (selectedTicket?.id === ticketId) {
        const res = await API.get(`/provisioning-tickets/${ticketId}`);
        setSelectedTicket(res.data);
      }
    } catch (err) {
      toast.error('Failed to assign ticket');
    }
  };

  const handleComplete = async (ticketId) => {
    setLoading(true);
    try {
      await API.put(`/provisioning-tickets/${ticketId}/complete`, {
        resolution_notes: resolutionNotes
      });
      toast.success('Ticket completed - changes applied');
      setShowTicketDialog(false);
      loadTickets();
      loadStats();
    } catch (err) {
      toast.error('Failed to complete ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (ticketId, status) => {
    try {
      await API.put(`/provisioning-tickets/${ticketId}`, { status });
      toast.success(`Ticket marked as ${status}`);
      loadTickets();
      loadStats();
      if (selectedTicket?.id === ticketId) {
        const res = await API.get(`/provisioning-tickets/${ticketId}`);
        setSelectedTicket(res.data);
      }
    } catch (err) {
      toast.error('Failed to update ticket');
    }
  };

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-slate-100 text-navy-600',
      failed: 'bg-red-100 text-red-700'
    };
    return <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${styles[status] || styles.pending}`}>{status.replace('_', ' ')}</span>;
  };

  const priorityBadge = (priority) => {
    const styles = {
      low: 'bg-slate-100 text-navy-600',
      medium: 'bg-blue-100 text-blue-700',
      high: 'bg-orange-100 text-orange-700',
      critical: 'bg-red-100 text-red-700'
    };
    return <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${styles[priority] || styles.medium}`}>{priority}</span>;
  };

  const actionIcon = (action) => {
    return action === 'provision' 
      ? <UserPlus size={14} className="text-emerald-500" />
      : <UserMinus size={14} className="text-red-500" />;
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleAutoResolvePreview = async () => {
    setAutoResolving(true);
    try {
      const res = await API.post('/provisioning-tickets/auto-resolve/preview');
      setAutoResolvePreview(res.data);
      setShowAutoResolveDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to analyze tickets');
    } finally {
      setAutoResolving(false);
    }
  };

  const handleAutoResolveExecute = async () => {
    setAutoResolving(true);
    try {
      const res = await API.post('/provisioning-tickets/auto-resolve/execute');
      toast.success(`Cleared ${res.data.total_cleared} tickets (${res.data.resolved} resolved, ${res.data.duplicates_cleared} duplicates). ${res.data.remaining_pending} remaining.`);
      setShowAutoResolveDialog(false);
      setAutoResolvePreview(null);
      loadTickets();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-resolve failed');
    } finally {
      setAutoResolving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="provisioning-tickets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Provisioning Tickets <InfoTooltip concept="provisioning" />
          </h1>
          <p className="text-sm text-navy-500 mt-1">
            Manual provisioning tasks for non-Azure systems
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleAutoResolvePreview} disabled={autoResolving} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="auto-resolve-btn">
            {autoResolving ? <SpinnerGap size={14} className="mr-1.5 animate-spin" /> : <Broom size={14} className="mr-1.5" />}
            Auto-Resolve
          </Button>
          <Button onClick={() => { loadTickets(); loadStats(); }} variant="outline" className="rounded-sm">
            <ArrowsClockwise size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <StatCard 
            icon={Ticket} 
            label="Total Tickets" 
            value={stats.total} 
            onClick={() => { setStatusFilter('all'); setAssignedToMeFilter(false); setPage(1); }}
          />
          <StatCard 
            icon={Clock} 
            label="Pending" 
            value={stats.pending} 
            accent 
            active={statusFilter === 'pending' && !assignedToMeFilter}
            onClick={() => { setStatusFilter('pending'); setAssignedToMeFilter(false); setPage(1); }}
          />
          <StatCard 
            icon={PlayCircle} 
            label="In Progress" 
            value={stats.in_progress} 
            active={statusFilter === 'in_progress' && !assignedToMeFilter}
            onClick={() => { setStatusFilter('in_progress'); setAssignedToMeFilter(false); setPage(1); }}
          />
          <StatCard 
            icon={UserCircle} 
            label="Assigned to Me" 
            value={stats.assigned_to_me || 0} 
            highlight
            active={assignedToMeFilter}
            onClick={() => { setStatusFilter('all'); setAssignedToMeFilter(true); setPage(1); }}
          />
          <StatCard 
            icon={CheckCircle} 
            label="Completed Today" 
            value={stats.completed_today} 
            onClick={() => { setStatusFilter('completed'); setAssignedToMeFilter(false); setPage(1); }}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-sm p-3">
        <div className="flex items-center gap-2">
          <Funnel size={14} className="text-navy-400" />
          <span className="text-xs font-semibold text-navy-500 uppercase">Filters:</span>
        </div>
        
        <Button 
          variant={assignedToMeFilter ? "default" : "outline"} 
          size="sm" 
          onClick={() => { setAssignedToMeFilter(!assignedToMeFilter); setPage(1); }}
          className={`h-8 text-xs rounded-sm ${assignedToMeFilter ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}`}
        >
          <UserCircle size={14} className="mr-1" /> Assigned to Me
        </Button>
        
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-32 h-8 text-xs rounded-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>

        <Select value={priorityFilter || "all_priority"} onValueChange={(v) => { setPriorityFilter(v === "all_priority" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-32 h-8 text-xs rounded-sm">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_priority">All Priority</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sourceFilter || "all_source"} onValueChange={(v) => { setSourceFilter(v === "all_source" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-32 h-8 text-xs rounded-sm">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_source">All Sources</SelectItem>
            <SelectItem value="ldap">LDAP</SelectItem>
            <SelectItem value="okta">Okta</SelectItem>
            <SelectItem value="servicenow">ServiceNow</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
            <SelectItem value="import">Import</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex-1" />

        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-navy-400" />
          <Input
            placeholder="Search tickets..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-8 h-8 text-xs w-48 rounded-sm"
          />
        </div>
      </div>

      {/* Tickets Table */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className="h-1.5 bg-navy-600" />
        <Table wrapperClassName="max-h-[calc(100vh-340px)]">
          <TableHeader>
            <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Ticket</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Action</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Object</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Target Identity</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Source</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Priority</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Status</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Assigned To</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}><EmptyState type="provisioning" /></TableCell>
              </TableRow>
            ) : tickets.map(ticket => (
              <TableRow key={ticket.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openTicket(ticket)}>
                <TableCell>
                  <div className="font-mono text-xs font-bold text-orange-600">{ticket.ticket_number}</div>
                  <div className="text-[10px] text-navy-400">{new Date(ticket.created_at).toLocaleDateString()}</div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    {actionIcon(ticket.action)}
                    <span className="text-xs capitalize">{ticket.action}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm font-medium text-navy-900">{ticket.object_name}</div>
                  <div className="text-[10px] text-navy-400 capitalize">{ticket.object_type}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-navy-700">{ticket.target_identity_name || '-'}</div>
                  <div className="text-[10px] text-navy-400">{ticket.target_identity_email || ''}</div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-[10px] uppercase">{ticket.source}</Badge>
                </TableCell>
                <TableCell>{priorityBadge(ticket.priority)}</TableCell>
                <TableCell>{statusBadge(ticket.status)}</TableCell>
                <TableCell>
                  <div className="text-xs text-navy-600">{ticket.assigned_to_name || '-'}</div>
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  {ticket.status === 'pending' && (
                    <Button size="sm" variant="ghost" onClick={() => handleAssign(ticket.id)} className="h-7 text-xs">
                      <User size={12} className="mr-1" /> Assign to me
                    </Button>
                  )}
                  {ticket.status === 'in_progress' && (
                    <Button size="sm" onClick={() => openTicket(ticket)} className="h-7 text-xs bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm">
                      <CheckCircle size={12} className="mr-1" /> Complete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-navy-500">
            Showing {((page - 1) * PAGE_SIZE) + 1} to {Math.min(page * PAGE_SIZE, total)} of {total} tickets
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1} className="h-7 rounded-sm">
              <CaretLeft size={14} /> Prev
            </Button>
            <span className="text-sm text-navy-600">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} className="h-7 rounded-sm">
              Next <CaretRight size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Ticket Detail Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Ticket size={20} className="text-orange-500" />
              {selectedTicket?.ticket_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="space-y-4 py-2">
              {/* Status & Priority */}
              <div className="flex items-center gap-3">
                {statusBadge(selectedTicket.status)}
                {priorityBadge(selectedTicket.priority)}
                <div className="flex items-center gap-1.5 text-sm">
                  {actionIcon(selectedTicket.action)}
                  <span className="capitalize font-medium">{selectedTicket.action}</span>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-navy-500">Object</Label>
                  <p className="mt-1 font-medium">{selectedTicket.object_name}</p>
                  <p className="text-xs text-navy-400 capitalize">{selectedTicket.object_type}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-navy-500">Target Identity</Label>
                  <p className="mt-1 font-medium">{selectedTicket.target_identity_name || '-'}</p>
                  <p className="text-xs text-navy-400">{selectedTicket.target_identity_email || ''}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-navy-500">Source System</Label>
                  <p className="mt-1">
                    <Badge variant="outline" className="uppercase">{selectedTicket.source}</Badge>
                  </p>
                  {selectedTicket.source_system_id && (
                    <p className="text-xs text-navy-400 mt-1">ID: {selectedTicket.source_system_id}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-navy-500">Assigned To</Label>
                  <p className="mt-1">{selectedTicket.assigned_to_name || 'Unassigned'}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-navy-500">Created</Label>
                  <p className="mt-1">{new Date(selectedTicket.created_at).toLocaleString()}</p>
                  <p className="text-xs text-navy-400">by {selectedTicket.created_by_name}</p>
                </div>
                {selectedTicket.completed_at && (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-navy-500">Completed</Label>
                    <p className="mt-1">{new Date(selectedTicket.completed_at).toLocaleString()}</p>
                  </div>
                )}
              </div>

              {/* Notes */}
              {selectedTicket.notes && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-navy-500">Notes</Label>
                  <p className="mt-1 text-sm text-navy-600 bg-slate-50 p-2 rounded-sm">{selectedTicket.notes}</p>
                </div>
              )}

              {/* Resolution Notes */}
              {selectedTicket.status === 'in_progress' || selectedTicket.status === 'pending' ? (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-navy-500">Resolution Notes</Label>
                  <Textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder="Add notes about how this ticket was resolved..."
                    className="mt-1 rounded-sm"
                    rows={3}
                  />
                </div>
              ) : selectedTicket.resolution_notes && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-navy-500">Resolution Notes</Label>
                  <p className="mt-1 text-sm text-navy-600 bg-slate-50 p-2 rounded-sm">{selectedTicket.resolution_notes}</p>
                </div>
              )}

              {/* Instructions */}
              {(selectedTicket.status === 'pending' || selectedTicket.status === 'in_progress') && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                  <p className="text-xs font-semibold text-amber-800 mb-2">
                    <Warning size={12} className="inline mr-1" />
                    Manual Action Required
                  </p>
                  <p className="text-xs text-amber-700">
                    {selectedTicket.action === 'provision' 
                      ? `Please manually add "${selectedTicket.target_identity_name}" to "${selectedTicket.object_name}" in the ${selectedTicket.source.toUpperCase()} system.`
                      : `Please manually remove "${selectedTicket.target_identity_name}" from "${selectedTicket.object_name}" in the ${selectedTicket.source.toUpperCase()} system.`
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {selectedTicket?.status === 'pending' && (
              <>
                <Button variant="outline" onClick={() => handleAssign(selectedTicket.id)} className="rounded-sm">
                  <User size={14} className="mr-1" /> Assign to Me
                </Button>
                <Button variant="outline" onClick={() => handleUpdateStatus(selectedTicket.id, 'cancelled')} className="rounded-sm text-navy-500">
                  Cancel Ticket
                </Button>
              </>
            )}
            {selectedTicket?.status === 'in_progress' && (
              <>
                <Button variant="outline" onClick={() => handleUpdateStatus(selectedTicket.id, 'failed')} className="rounded-sm text-red-500">
                  <XCircle size={14} className="mr-1" /> Mark Failed
                </Button>
                <Button onClick={() => handleComplete(selectedTicket.id)} disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm">
                  {loading ? <SpinnerGap size={14} className="mr-1 animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
                  Complete & Apply
                </Button>
              </>
            )}
            {(selectedTicket?.status === 'completed' || selectedTicket?.status === 'cancelled' || selectedTicket?.status === 'failed') && (
              <Button variant="outline" onClick={() => setShowTicketDialog(false)} className="rounded-sm">
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Resolve Dialog */}
      <Dialog open={showAutoResolveDialog} onOpenChange={setShowAutoResolveDialog}>
        <DialogContent className="max-w-lg" data-testid="auto-resolve-dialog" aria-describedby="auto-resolve-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Broom size={20} className="text-orange-500" />
              Auto-Resolve Tickets
            </DialogTitle>
          </DialogHeader>
          {autoResolvePreview && (
            <div className="space-y-4 pt-2" id="auto-resolve-desc">
              <p className="text-sm text-navy-600">
                Scanned <span className="font-bold text-navy-900">{autoResolvePreview.total_pending}</span> pending tickets and found:
              </p>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
                  <p className="text-2xl font-bold text-emerald-700">{autoResolvePreview.resolvable_count}</p>
                  <p className="text-[10px] text-emerald-600 uppercase tracking-widest font-semibold">State Already Matches</p>
                  <p className="text-xs text-emerald-600 mt-1">Requested action already fulfilled</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                  <p className="text-2xl font-bold text-blue-700">{autoResolvePreview.duplicate_count}</p>
                  <p className="text-[10px] text-blue-600 uppercase tracking-widest font-semibold">Duplicates</p>
                  <p className="text-xs text-blue-600 mt-1">Same action already queued</p>
                </div>
              </div>

              {/* Preview list */}
              {autoResolvePreview.resolvable_count + autoResolvePreview.duplicate_count > 0 ? (
                <>
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-sm divide-y divide-slate-100">
                    {autoResolvePreview.resolvable.slice(0, 10).map(t => (
                      <div key={t.id} className="px-3 py-2 flex items-center gap-2 text-xs">
                        <CheckCircle size={14} className="text-emerald-500 shrink-0" />
                        <span className="text-navy-700 font-medium truncate">{t.ticket_number}</span>
                        <span className="text-navy-400 truncate flex-1">{t.object_name} → {t.target_identity_name}</span>
                        <Badge className="bg-emerald-100 text-emerald-700 text-[9px] shrink-0">
                          {t.reason === 'already_provisioned' ? 'Already done' : 'Already removed'}
                        </Badge>
                      </div>
                    ))}
                    {autoResolvePreview.duplicates.slice(0, 5).map(t => (
                      <div key={t.id} className="px-3 py-2 flex items-center gap-2 text-xs">
                        <ArrowsClockwise size={14} className="text-blue-500 shrink-0" />
                        <span className="text-navy-700 font-medium truncate">{t.ticket_number}</span>
                        <span className="text-navy-400 truncate flex-1">Duplicate of {t.original_ticket}</span>
                        <Badge className="bg-blue-100 text-blue-700 text-[9px] shrink-0">Duplicate</Badge>
                      </div>
                    ))}
                    {(autoResolvePreview.resolvable_count + autoResolvePreview.duplicate_count) > 15 && (
                      <div className="px-3 py-2 text-center text-[10px] text-navy-400">
                        +{autoResolvePreview.resolvable_count + autoResolvePreview.duplicate_count - 15} more tickets
                      </div>
                    )}
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start gap-2">
                    <Warning size={16} className="text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      This will mark <span className="font-bold">{autoResolvePreview.resolvable_count + autoResolvePreview.duplicate_count} tickets</span> as completed.
                      Remaining: <span className="font-bold">{autoResolvePreview.total_pending - autoResolvePreview.resolvable_count - autoResolvePreview.duplicate_count}</span> tickets still need manual review.
                    </p>
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAutoResolveDialog(false)} className="rounded-sm">Cancel</Button>
                    <Button onClick={handleAutoResolveExecute} disabled={autoResolving}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm" data-testid="auto-resolve-confirm-btn">
                      {autoResolving ? <SpinnerGap size={14} className="mr-1.5 animate-spin" /> : <Lightning size={14} className="mr-1.5" />}
                      Resolve {autoResolvePreview.resolvable_count + autoResolvePreview.duplicate_count} Tickets
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <div className="text-center py-6">
                  <CheckCircle size={32} className="mx-auto mb-2 text-emerald-400" />
                  <p className="text-sm font-medium text-navy-900">All tickets require manual review</p>
                  <p className="text-xs text-navy-500 mt-1">No tickets could be automatically resolved at this time.</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, highlight, active, onClick }) {
  const baseClasses = 'rounded-sm border p-4 transition-all duration-150 cursor-pointer hover:shadow-md';
  const colorClasses = active 
    ? 'bg-slate-800 border-slate-700 ring-2 ring-slate-500'
    : highlight 
      ? 'bg-blue-50 border-blue-200 hover:border-blue-300' 
      : accent 
        ? 'bg-orange-50 border-orange-200 hover:border-orange-300' 
        : 'bg-white border-slate-200 hover:border-slate-300';
  
  return (
    <div className={`${baseClasses} ${colorClasses}`} onClick={onClick}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-sm ${active ? 'bg-slate-700' : highlight ? 'bg-blue-100' : accent ? 'bg-orange-100' : 'bg-slate-100'}`}>
          <Icon size={18} className={active ? 'text-white' : highlight ? 'text-blue-600' : accent ? 'text-orange-600' : 'text-navy-500'} />
        </div>
        <div>
          <p className={`text-2xl font-bold ${active ? 'text-white' : 'text-navy-900'}`}>{value}</p>
          <p className={`text-xs uppercase tracking-wider ${active ? 'text-navy-300' : 'text-navy-500'}`}>{label}</p>
        </div>
      </div>
    </div>
  );
}
